import type { JSX, ReactNode } from "react";

interface SectionCardProps {
  title: string;
  /** One muted line under the title: what this block measures, or when it was last checked. */
  caption?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  titleTestId?: string;
}

/**
 * The one card shell every report block sits in — the premium recipe from
 * `docs/design/templates.md` (14px radius, `--premium-border`, `--sh-card`)
 * with a bordered header row. Blocks differ only in their body.
 */
export function SectionCard({
  title,
  caption,
  actions,
  children,
  titleTestId,
}: SectionCardProps): JSX.Element {
  return (
    <div className="rounded-[14px] border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-card)]">
      <header className="flex items-center justify-between gap-3 border-b border-[var(--premium-border)] px-5 py-3">
        <div className="min-w-0">
          <h2
            className="text-sm font-semibold text-[var(--text-primary)]"
            data-testid={titleTestId}
          >
            {title}
          </h2>
          {caption !== undefined && (
            <p className="mt-0.5 text-xs text-[var(--text-subtle)]">{caption}</p>
          )}
        </div>
        {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}
